import { zodResolver } from "@hookform/resolvers/zod";

import LightModeIcon from "@mui/icons-material/LightMode";

import DarkModeIcon from "@mui/icons-material/DarkMode";

import ExtensionIcon from "@mui/icons-material/Extension";

import ShieldIcon from "@mui/icons-material/Shield";

import LockIcon from "@mui/icons-material/Lock";

import TuneIcon from "@mui/icons-material/Tune";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useForm } from "react-hook-form";

import { Link, Navigate, useSearchParams } from "react-router-dom";

import { z } from "zod";

import { authApi } from "@/api";

import { MODULE_HUB_PATH } from "@/constants/authRoutes";

import { platformApi, type OrganizationFeatureDto } from "@/api/platform.api";

import { PageHeader } from "@/components/layout/PageHeader";

import { SettingsLayout } from "@/components/settings/SettingsLayout";

import { SettingsPanel } from "@/components/settings/SettingsPanel";

import type { SettingsNavItem } from "@/components/settings/SettingsNav";

import { Button } from "@/components/ui/Button";

import { Input } from "@/components/ui/Input";

import { Badge } from "@/components/ui/Badge";

import { TooltipText } from "@/components/ui/Tooltip";

import { ViewModeToggle } from "@/components/ui/ViewModeToggle";

import { useAppDispatch } from "@/hooks/useAppDispatch";

import { useAppSelector } from "@/hooks/useAppSelector";

import { useWorkspaceModule } from "@/hooks/useWorkspaceModule";

import {
  featuresForModule,
  moduleSubscriptionLink,
} from "@/config/moduleWorkspace";

import { getModuleEntryByErpModule } from "@/config/moduleEntries";

import { setTheme } from "@/store/appSlice";

import { useEffect, useMemo, useState } from "react";

import { showApiError } from "@/utils/apiError";

import { notify } from "@/core/toast/notify";

import { SampleDataPanel } from "@/components/ui/SampleDataPanel";

import { MfaSettingsPanel } from "@/components/security/MfaSettingsPanel";

import { setMfaEnabled } from "@/store/authSlice";

import { Skeleton } from "@/components/ui/Skeleton";

const SECTIONS = [
  "preferences",
  "features",
  "security",
  "account",
] as const;

type SettingsSection = (typeof SECTIONS)[number];

const schema = z

  .object({
    currentPassword: z.string().min(1, "Required"),

    newPassword: z.string().min(8, "At least 8 characters"),

    confirmPassword: z.string(),
  })

  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords must match",

    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

function featureStatus(feature: OrganizationFeatureDto) {
  if (feature.enabled) {
    return { label: "Included", variant: "success" as const };
  }

  if (feature.locked) {
    return { label: "Upgrade required", variant: "warning" as const };
  }

  return { label: "Unavailable", variant: "default" as const };
}

function isSettingsSection(value: string | null): value is SettingsSection {
  return SECTIONS.includes(value as SettingsSection);
}

export function ModuleWorkspaceSettingsPage() {
  const dispatch = useAppDispatch();

  const theme = useAppSelector((s) => s.app.theme);

  const mfaEnabled = useAppSelector((s) => s.auth.mfaEnabled);

  const { module, moduleLabel, ready } = useWorkspaceModule();

  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);

  const [features, setFeatures] = useState<OrganizationFeatureDto[]>([]);

  const [featuresLoading, setFeaturesLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const sectionParam = searchParams.get("section");

  const activeSection: SettingsSection = isSettingsSection(sectionParam)
    ? sectionParam
    : "preferences";

  const homePath = module
    ? (getModuleEntryByErpModule(module)?.homePath ?? "/org")
    : "/org";

  const moduleFeatures = module ? featuresForModule(features, module) : [];

  const navItems: SettingsNavItem[] = useMemo(
    () => [
      {
        id: "preferences",

        label: "Preferences",

        description: "Theme and guided demos",

        icon: <TuneIcon sx={{ fontSize: 18 }} />,
      },

      {
        id: "features",

        label: "Plan & features",

        description: "What is included in your plan",

        icon: <ExtensionIcon sx={{ fontSize: 18 }} />,
      },

      {
        id: "security",

        label: "Security",

        description: "Two-factor authentication",

        icon: <ShieldIcon sx={{ fontSize: 18 }} />,
      },

      {
        id: "account",

        label: "Password",

        description: "Update your sign-in password",

        icon: <LockIcon sx={{ fontSize: 18 }} />,
      },
    ],

    [],
  );

  const selectSection = (id: string) => {
    setSearchParams(id === "preferences" ? {} : { section: id }, {
      replace: true,
    });
  };

  useEffect(() => {
    if (!module) return;

    setFeaturesLoading(true);

    platformApi

      .listFeatures()

      .then(setFeatures)

      .catch(() => undefined)

      .finally(() => setFeaturesLoading(false));
  }, [module]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);

    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,

        newPassword: data.newPassword,
      });

      notify.success("Password changed");

      reset();
    } catch (err) {
      showApiError(err, "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  if (!ready || !module) {
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }

  return (
    <div className="page-content">
      <PageHeader
        title="Settings"
        description={`Manage your workspace preferences, security, and ${moduleLabel} feature access.`}
        breadcrumbs={[
          { label: moduleLabel, to: homePath },

          { label: "Settings" },
        ]}
      />

      <SettingsLayout
        nav={navItems}
        activeId={activeSection}
        onSelect={selectSection}
      >
        {activeSection === "preferences" && (
          <SettingsPanel
            id="settings-panel-preferences"
            title="Preferences"
            description="Customize how the workspace looks and whether guided sample flows are shown."
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Appearance
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Choose a light or dark theme. Your choice is saved on this
                  device.
                </p>

                <ViewModeToggle
                  className="mt-4"
                  aria-label="Theme"
                  value={theme}
                  onChange={(mode) =>
                    dispatch(setTheme(mode as "light" | "dark"))
                  }
                  options={[
                    {
                      value: "light",
                      label: "Light",
                      icon: <LightModeIcon sx={{ fontSize: 18 }} />,
                    },

                    {
                      value: "dark",
                      label: "Dark",
                      icon: <DarkModeIcon sx={{ fontSize: 18 }} />,
                    },
                  ]}
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Sample data guides
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Explore modules safely with example flows — no production data
                  is changed.
                </p>

                <SampleDataPanel className="mt-4" compact />
              </div>
            </div>
          </SettingsPanel>
        )}

        {activeSection === "features" && (
          <SettingsPanel
            id="settings-panel-features"
            title={`${moduleLabel} features`}
            description="Features included in your current plan. Upgrade your subscription to unlock additional capabilities."
            actions={
              <Link
                to={moduleSubscriptionLink(module)}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline focus:outline-none focus-visible:underline dark:text-primary-400"
              >
                Manage subscription
                <ArrowForwardIcon sx={{ fontSize: 16 }} aria-hidden />
              </Link>
            }
          >
            {featuresLoading ? (
              <div
                className="grid gap-3 sm:grid-cols-2"
                aria-busy="true"
                aria-label="Loading features"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 rounded-lg" />
                ))}
              </div>
            ) : moduleFeatures.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No feature flags are configured for this module yet.
              </p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2" role="list">
                {moduleFeatures.map((feature) => {
                  const status = featureStatus(feature);

                  return (
                    <li
                      key={feature.code}
                      className="rounded-lg border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-700 dark:bg-slate-800/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {feature.name}
                          </p>

                          <TooltipText
                            text={feature.code}
                            className="mt-0.5 text-xs text-slate-500 dark:text-slate-400"
                          />
                        </div>

                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SettingsPanel>
        )}

        {activeSection === "security" && (
          <SettingsPanel
            id="settings-panel-security"
            title="Security"
            description="Add an extra layer of protection to your account."
          >
            <MfaSettingsPanel
              initialEnabled={mfaEnabled}
              onStatusChange={(enabled) => dispatch(setMfaEnabled(enabled))}
            />
          </SettingsPanel>
        )}

        {activeSection === "account" && (
          <SettingsPanel
            id="settings-panel-account"
            title="Change password"
            description="Use a strong password with at least 8 characters."
          >
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="max-w-md space-y-4"
              noValidate
            >
              <Input
                label="Current password"
                type="password"
                autoComplete="current-password"
                {...register("currentPassword")}
                error={errors.currentPassword?.message}
              />

              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                {...register("newPassword")}
                error={errors.newPassword?.message}
              />

              <Input
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                {...register("confirmPassword")}
                error={errors.confirmPassword?.message}
              />

              <Button type="submit" loading={loading}>
                Update password
              </Button>
            </form>
          </SettingsPanel>
        )}
      </SettingsLayout>
    </div>
  );
}
